import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  EvaluationAuthorDataWrapper,
  EvaluationCriteria,
  EvaluationCriteriaDataItemRequest,
  EvaluationItemOption,
  EvaluationOverview,
  EvaluationReport,
  EvaluationReportAuthor,
} from '@/module/Evaluation/model/EvaluationTypes';

import { Id, PersistentFile, getQueryKey } from '@feature/utils';

import * as api from './EvaluationApi';

const evaluationKey = getQueryKey('evaluation');

export function getEvaluationsOptions(): UseSuspenseQueryOptions<EvaluationReport[]> {
  return {
    queryFn: () => api.getAll(),
    queryKey: evaluationKey.list(),
  };
}

export function getEvaluationOptions(evaluationId: Id): UseSuspenseQueryOptions<EvaluationReport> {
  return {
    queryFn: () => api.get(evaluationId),
    queryKey: evaluationKey.one(evaluationId),
  };
}

export function getAuthorEvaluationOptions(
  evaluationId: Id,
  authorId: Id,
): UseSuspenseQueryOptions<EvaluationReportAuthor | null> {
  return {
    queryFn: () => api.getForAuthor(evaluationId, authorId),
    queryKey: evaluationKey.as([evaluationId, 'author', authorId]),
  };
}

export function getEvaluationDepartmentSummaryFileOptions(
  evaluationId: Id,
  departmentId: Id,
): UseSuspenseQueryOptions<PersistentFile | null> {
  return {
    queryFn: () => api.getDepartmentSummaryFile(evaluationId, departmentId),
    queryKey: evaluationKey.as([evaluationId, 'summary', departmentId]),
  };
}

export function getEvaluationCriteriaOptions(evaluationId: Id): UseSuspenseQueryOptions<EvaluationCriteria[]> {
  return {
    queryFn: () => api.getCriteria(evaluationId),
    queryKey: evaluationKey.as([evaluationId, 'criteria']),
    staleTime: 5000, // wait 5 seconds before considering the request stale (this deduplicates all the loader provisioning done by the router)
  };
}

export function getEvaluationOverviewOptions(
  evaluationId: Id,
  authorId: Id,
): UseSuspenseQueryOptions<EvaluationOverview> {
  return {
    queryFn: () => api.getEvaluationOverview(evaluationId, authorId),
    queryKey: evaluationKey.as([evaluationId, 'author', authorId, 'overview']),
  };
}

export function getEvaluationCriteriaDataOptions(
  evaluationId: Id,
  authorId: Id,
  orderNo: number,
): UseSuspenseQueryOptions<EvaluationAuthorDataWrapper> {
  return {
    queryFn: () => api.getCriteriaData(evaluationId, authorId, orderNo),
    queryKey: evaluationKey.as([evaluationId, 'author', authorId, 'data', orderNo]),
  };
}

export function getEvaluationCriteriaPossibleItemsOptions<T>(
  evaluationId: Id,
  authorId: Id,
  orderNo: number,
): UseSuspenseQueryOptions<EvaluationItemOption<T>[]> {
  return {
    queryFn: () => api.getPossibleItemOptions<T>(evaluationId, authorId, orderNo),
    queryKey: evaluationKey.as([evaluationId, 'author', authorId, 'data', orderNo, 'possible-options']),
  };
}

export function useEvaluationOpen(year: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.open(year),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationStart(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.start(evaluationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationUploadCollegialFile(evaluationId: Id, authorId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadCollegialFile(evaluationId, authorId, file),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationUploadChiefFile(evaluationId: Id, authorId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadChiefFile(evaluationId, authorId, file),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationUploadDisciplineAdminSignedFile(evaluationId: Id, authorId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadDisciplineAdminSignedFile(evaluationId, authorId, file),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationUploadDepartmentAdminSignedFile(evaluationId: Id, authorId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadDepartmentAdminSignedFile(evaluationId, authorId, file),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationUploadDepartmentSummaryFile(evaluationId: Id, departmentId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadDepartmentSummaryFile(evaluationId, departmentId, file),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationAddCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: EvaluationCriteriaDataItemRequest) =>
      api.addCriteriaItem(evaluationId, authorId, orderNo, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationRecalculateCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: Id) => api.recalculateCriteriaItem(evaluationId, authorId, orderNo, itemId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationEditCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number, itemId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: EvaluationCriteriaDataItemRequest) =>
      api.editCriteriaItem(evaluationId, authorId, orderNo, itemId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useEvaluationDeleteCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: Id) => api.deleteCriteriaItem(evaluationId, authorId, orderNo, itemId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}
